import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { AuthContextType, AuthResponse, RoleViewMode, User } from '../types/auth';
import { authAPI } from '../services/auth';
import { AUTH_EXPIRED_EVENT, setAuthRefreshHandler } from '../services/api';
import { completeMicrosoftSignIn, refreshMicrosoftSignIn, restoreMicrosoftSignIn, signInWithMicrosoft } from '../services/entraAuth';
import { getStoredRoleViewMode, setStoredRoleViewMode } from '../services/roleViewMode';
import { AuthAccountChangedError, authAccountFromToken } from '../services/authAccount';

interface JwtPayload {
  sub?: string;
  nameid?: string;
  email?: string;
  name?: string;
  exp?: number;
  role?: string | string[];
  roles?: string | string[];
  [claim: string]: unknown;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AUTH_USER_STORAGE_KEY = 'authUser';
const ADMIN_ROLE = 'admin';
const REPORT_VIEWER_ROLE = 'reportviewer';

const hasRole = (roles: string[], roleName: string) => roles.some(role => role.toLowerCase() === roleName);
const canViewReportsForRoles = (roles: string[]) => hasRole(roles, ADMIN_ROLE) || hasRole(roles, REPORT_VIEWER_ROLE);

const getClaim = (payload: JwtPayload, ...names: string[]) => {
  for (const name of names) {
    const value = payload[name];
    if (typeof value === 'string') {
      return value;
    }
  }
  return '';
};

const getClaimValues = (payload: JwtPayload, ...names: string[]) => {
  return names.flatMap(name => {
    const value = payload[name];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }
    return typeof value === 'string' ? [value] : [];
  });
};

const userFromToken = (token: string, fallbackEmail = ''): User => {
  const decoded = jwtDecode<JwtPayload>(token);
  const roles = Array.from(new Set(getClaimValues(
    decoded,
    'role',
    'roles',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
  )));

  return {
    id: getClaim(decoded, 'sub', 'oid', 'nameid', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'),
    email: getClaim(decoded, 'email', 'preferred_username', 'upn', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress') || fallbackEmail,
    name: getClaim(decoded, 'name', 'unique_name', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'),
    roles,
    isAdmin: hasRole(roles, ADMIN_ROLE),
    canViewAllReports: canViewReportsForRoles(roles),
  };
};

const userFromAuthResponse = (response: AuthResponse): User => {
  const tokenUser = userFromToken(response.token, response.email);
  const roles = Array.from(new Set(response.roles ?? []));
  return {
    ...tokenUser,
    email: response.email || tokenUser.email,
    name: response.name || tokenUser.name,
    roles,
    isAdmin: hasRole(roles, ADMIN_ROLE),
    canViewAllReports: canViewReportsForRoles(roles),
  };
};

const readStoredUser = (storedUser: string | null, storedToken: string): User => {
  const tokenUser = userFromToken(storedToken);

  if (!storedUser) {
    return tokenUser;
  }

  try {
    const parsed = JSON.parse(storedUser) as Partial<User> | null;
    if (!parsed || typeof parsed !== 'object') {
      return tokenUser;
    }

    const roles = Array.isArray(parsed.roles)
      ? parsed.roles.filter((role): role is string => typeof role === 'string')
      : tokenUser.roles;

    return {
      id: typeof parsed.id === 'string' && parsed.id ? parsed.id : tokenUser.id,
      email: typeof parsed.email === 'string' && parsed.email ? parsed.email : tokenUser.email,
      name: typeof parsed.name === 'string' ? parsed.name : tokenUser.name,
      roles,
      isAdmin: hasRole(roles, ADMIN_ROLE),
      canViewAllReports: canViewReportsForRoles(roles),
    };
  } catch {
    return tokenUser;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [roleViewMode, setRoleViewModeState] = useState<RoleViewMode>(() => getStoredRoleViewMode());
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const sessionGeneration = useRef(0);

  const actualIsAdmin = user?.isAdmin ?? false;
  const effectiveRoleViewMode = actualIsAdmin ? roleViewMode : 'User';
  const isAdmin = actualIsAdmin && effectiveRoleViewMode === 'Admin';
  const canManageProjectPlanning = actualIsAdmin ? isAdmin : hasRole(user?.roles ?? [], 'projectmanager');
  const hasReportViewerRole = user?.roles.some(role => role.toLowerCase() === REPORT_VIEWER_ROLE) ?? false;
  // [FIX:leads] Admin's simulated User mode must override any additional viewer role,
  // matching the server-side view-mode authorization policy.
  const canViewAllReports = actualIsAdmin
    ? effectiveRoleViewMode !== 'User'
    : hasReportViewerRole;

  const clearAuthenticatedState = () => {
    ++sessionGeneration.current;
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
  };

  const storeAuthenticatedResponse = useCallback((response: AuthResponse) => {
    const authenticatedUser = userFromAuthResponse(response);
    ++sessionGeneration.current;
    setToken(response.token);
    setUser(authenticatedUser);
    localStorage.setItem('token', response.token);
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(authenticatedUser));
  }, []);

  useEffect(() => {
    let active = true;

    const completeBackendLogin = async (idToken: string) => {
      const response: AuthResponse = await authAPI.loginWithEntra(idToken);
      if (!active) {
        return;
      }

      storeAuthenticatedResponse(response);
    };

    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decoded = jwtDecode<JwtPayload>(storedToken);
          if (decoded.exp && decoded.exp > Date.now() / 1000) {
            const storedUser = localStorage.getItem(AUTH_USER_STORAGE_KEY);
            setToken(storedToken);
            setUser(readStoredUser(storedUser, storedToken));
            setIsLoading(false);
            return;
          }
          localStorage.removeItem('token');
          localStorage.removeItem(AUTH_USER_STORAGE_KEY);
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem(AUTH_USER_STORAGE_KEY);
        }
      }

      try {
        const idToken = await completeMicrosoftSignIn();
        if (idToken) {
          await completeBackendLogin(idToken);
          return;
        }

        const restoredToken = await restoreMicrosoftSignIn();
        if (restoredToken) {
          await completeBackendLogin(restoredToken);
        }
      } catch (error) {
        if (active) {
          setAuthError(error instanceof Error ? error.message : 'Microsoft Entra sign-in failed');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      active = false;
    };
  }, [storeAuthenticatedResponse]);

  useEffect(() => {
    let active = true;
    setAuthRefreshHandler(async () => {
      const generation = sessionGeneration.current;
      const initialToken = localStorage.getItem('token');
      const initialAccount = authAccountFromToken(initialToken);
      const assertSessionCurrent = () => {
        if (!active || generation !== sessionGeneration.current || localStorage.getItem('token') !== initialToken) {
          throw new AuthAccountChangedError();
        }
      };
      const idToken = await refreshMicrosoftSignIn();
      assertSessionCurrent();
      if (!idToken) {
        return null;
      }

      const response = await authAPI.loginWithEntra(idToken);
      assertSessionCurrent();
      if (initialAccount && authAccountFromToken(response.token) !== initialAccount) {
        throw new AuthAccountChangedError();
      }
      storeAuthenticatedResponse(response);
      setAuthError('');
      return response.token;
    });

    return () => { active = false; setAuthRefreshHandler(null); };
  }, [storeAuthenticatedResponse]);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearAuthenticatedState();
      setAuthError('Your session expired. Please sign in again.');
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, []);

  const loginWithEntra = async () => {
    setIsLoading(true);
    setAuthError('');
    try {
      await signInWithMicrosoft();
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Microsoft Entra sign-in failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearAuthenticatedState();
  };

  const setRoleViewMode = (mode: RoleViewMode) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[FIX:leads] View mode changed', { mode });
    }
    setRoleViewModeState(mode);
    setStoredRoleViewMode(mode);
  };

  const clearAuthError = () => setAuthError('');

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loginWithEntra,
        logout,
        isLoading,
        isAuthenticated: !!user && !!token,
        isAdmin,
        actualIsAdmin,
        canViewAllReports,
        canManageProjectPlanning,
        roleViewMode: effectiveRoleViewMode,
        setRoleViewMode,
        authError,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
